// Terrain analysis math — elevation/slope/aspect from a sampled elevation grid.
// Pure functions so they're unit-testable without WebGL/Mapbox.

const FT_PER_M = 3.28084;

export function metersPerDegree(lat) {
  return { dLat: 111320, dLng: 111320 * Math.cos((lat * Math.PI) / 180) };
}

const COMPASS_8 = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];

export function cardinal(deg) {
  return COMPASS_8[Math.round((((deg % 360) + 360) % 360) / 45) % 8];
}

export function faceNote(card) {
  return {
    N: "North-facing — cooler, holds snow longer, shaded bedding cover",
    NE: "Northeast-facing — morning shade, stays cool into the day",
    E: "East-facing — catches morning sun, warms early",
    SE: "Southeast-facing — early sun, a classic cold-morning feeding slope",
    S: "South-facing — most sun, warmer and drier; midday feeding in cold weather",
    SW: "Southwest-facing — strong afternoon sun, dries first after storms",
    W: "West-facing — afternoon sun and often the prevailing wind",
    NW: "Northwest-facing — coolest afternoons, snow lingers here",
  }[card] || "";
}

/**
 * Horn's method on a 3×3 elevation neighborhood (standard GIS slope/aspect).
 * z rows are north→south, cols west→east (meters), cellX/cellY = spacing in meters.
 * Returns { slopeDeg, slopePct, aspectDeg (downslope compass bearing, null when flat) }.
 */
export function hornSlopeAspect(z, cellX, cellY = cellX) {
  const [a, b, c, d, , f, g, h, i] = z;
  const dzdx = (c + 2 * f + i - (a + 2 * d + g)) / (8 * cellX);
  // dz/dy as change per meter NORTHWARD (north row minus south row) — Horn's
  // classic formula assumes y increases down-grid; we flip it to compass space.
  const dzdy = (a + 2 * b + c - (g + 2 * h + i)) / (8 * cellY);
  const rise = Math.sqrt(dzdx * dzdx + dzdy * dzdy);
  const slopeDeg = (Math.atan(rise) * 180) / Math.PI;
  const slopePct = rise * 100;
  let aspectDeg = null;
  if (rise > 1e-6) {
    // downslope vector (-dzdx, -dzdy) in (east, north) → bearing clockwise from north
    aspectDeg = ((Math.atan2(-dzdx, -dzdy) * 180) / Math.PI + 360) % 360;
  }
  return { slopeDeg, slopePct, aspectDeg };
}

/**
 * Sample the DEM on a regular grid over `bounds` ({west, south, east, north}) and compute
 * slope per cell — the engine behind the slope heatmap. Returns GeoJSON point features
 * with a `slope` (degrees) property plus the sampling coverage ratio (0..1). Cells whose
 * neighborhood isn't fully loaded are skipped, so coverage < 1 means "zoom in".
 */
export function sampleSlopeGrid(elevFn, bounds, cols = 70, rows = 44) {
  const latMid = (bounds.south + bounds.north) / 2;
  const { dLat, dLng } = metersPerDegree(latMid);
  const stepLng = (bounds.east - bounds.west) / cols;
  const stepLat = (bounds.north - bounds.south) / rows;
  const cellX = Math.max(1, stepLng * dLng);
  const cellY = Math.max(1, stepLat * dLat);
  // elevations matrix with a 1-cell apron so every interior cell has full 3×3 support
  const E = [];
  let sampled = 0;
  const total = (rows + 2) * (cols + 2);
  for (let j = -1; j <= rows; j++) {
    const row = [];
    for (let i = -1; i <= cols; i++) {
      const e = elevFn({
        lng: bounds.west + (i + 0.5) * stepLng,
        lat: bounds.north - (j + 0.5) * stepLat,  // j=0 is the northernmost row
      });
      if (e != null && !Number.isNaN(e)) sampled++;
      row.push(e == null || Number.isNaN(e) ? null : e);
    }
    E.push(row);
  }
  const features = [];
  for (let j = 0; j < rows; j++) {
    for (let i = 0; i < cols; i++) {
      // 3×3 neighborhood centered on cell (j, i) within the apron matrix
      const n = [];
      let complete = true;
      for (let dj = 0; dj < 3 && complete; dj++) {
        for (let di = 0; di < 3; di++) {
          const v = E[j + dj][i + di];
          if (v == null) { complete = false; break; }
          n.push(v);
        }
      }
      if (!complete) continue;
      const { slopeDeg } = hornSlopeAspect(n, cellX, cellY);
      features.push({
        type: "Feature",
        properties: { slope: Math.min(45, slopeDeg) },
        geometry: { type: "Point", coordinates: [bounds.west + (i + 0.5) * stepLng, bounds.north - (j + 0.5) * stepLat] },
      });
    }
  }
  return { features, coverage: total ? sampled / total : 0, cells: features.length };
}

/**
 * Sample the DEM around (lng, lat) through elevFn(lngLat) -> meters|null and analyze.
 * Returns null if any neighbor tile is unloaded (caller should zoom in / wait for idle).
 */
export function analyzePoint(elevFn, lng, lat, cellMeters = 30) {
  const { dLat, dLng } = metersPerDegree(lat);
  const dLatDeg = cellMeters / dLat;
  const dLngDeg = cellMeters / dLng;
  const z = [];
  // rows north→south, cols west→east
  for (let j = 1; j >= -1; j--) {
    for (let i = -1; i <= 1; i++) {
      const e = elevFn({ lng: lng + i * dLngDeg, lat: lat + j * dLatDeg });
      if (e == null || Number.isNaN(e)) return null;
      z.push(e);
    }
  }
  const center = z[4];
  const { slopeDeg, slopePct, aspectDeg } = hornSlopeAspect(z, cellMeters);
  return {
    elevationM: center,
    elevationFt: center * FT_PER_M,
    slopeDeg,
    slopePct,
    aspectDeg,
    aspectCardinal: aspectDeg == null ? "Flat" : cardinal(aspectDeg),
    aspectNote: aspectDeg == null ? "Essentially flat ground" : faceNote(cardinal(aspectDeg)),
    lng, lat,
  };
}
